import React, { useState, useMemo, useEffect } from 'react';
import { format, parse } from 'date-fns';
import { 
  Calendar, Clock, User, Mail, CheckCircle2, ArrowLeft, 
  XCircle, Shield, Table, Check, Users, History, Settings, 
  PlusCircle, Trash2, Edit, LogOut, Share2, Star, ChevronDown, 
  MapPin, Palette, Download 
} from 'lucide-react';

// --- Firebase Imports ---
import { initializeApp } from 'firebase/app';
import { 
  getFirestore, collection, onSnapshot, addDoc, query, 
  doc, updateDoc, deleteDoc, writeBatch 
} from 'firebase/firestore';

// --- Firebase Configuration ---
const firebaseConfig = {
  apiKey: "AIzaSyCbrtpFVShWxD-r9aK9TX7Bp60pqje-aW4",
  authDomain: "lv-branding-events.firebaseapp.com",
  projectId: "lv-branding-events",
  storageBucket: "lv-branding-events.firebasestorage.app",
  messagingSenderId: "322145106472",
  appId: "1:322145106472:web:5ffced71e6fcc84a41931d"
};

// --- Initialize Firebase and Firestore ---
let app;
let db;
const isFirebaseConfigValid = firebaseConfig.apiKey && firebaseConfig.projectId;
if (isFirebaseConfigValid) {
    try {
        app = initializeApp(firebaseConfig);
        db = getFirestore(app);
    } catch (error) {
        console.error("Firebase initialization failed:", error);
    }
}

// --- Helper Functions ---
const convertTo24Hour = (time12h) => {
    if (!time12h || (!time12h.includes('AM') && !time12h.includes('PM'))) return time12h;
    const [time, modifier] = time12h.split(' ');
    let [hours, minutes] = time.split(':');
    if (hours === '12') hours = '00';
    if (modifier === 'PM') hours = parseInt(hours, 10) + 12;
    return `${String(hours).padStart(2, '0')}:${minutes}`;
};

const convertTo12Hour = (time24h) => {
    if (!time24h || time24h.includes('AM') || time24h.includes('PM')) return time24h;
    const [hours, minutes] = time24h.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    let displayHour = hour % 12;
    if (displayHour === 0) displayHour = 12;
    return `${displayHour}:${minutes} ${ampm}`;
};

// --- Main Application Component ---
export default function App() {
  const [view, setView] = useState('booking');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [lastBooking, setLastBooking] = useState(null);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [selectedTime, setSelectedTime] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [bookingError, setBookingError] = useState('');
  const [allBookings, setAllBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [activeEvent, setActiveEvent] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [collapsedLists, setCollapsedLists] = useState({});

  // Default theme color if not set
  const themeColor = activeEvent?.themeColor || '#f97316'; 

  // --- Data Fetching and Global Font ---
  useEffect(() => {
    // Inject Fira Sans font
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Fira+Sans:wght@300;400;500;600;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    document.body.style.fontFamily = "'Fira Sans', sans-serif";

    if (!db) return;
    
    // Subscribe to bookings
    const bookingsQuery = query(collection(db, "bookings"));
    const unsubscribeBookings = onSnapshot(bookingsQuery, (snapshot) => {
      const bookingsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllBookings(bookingsData);
    });

    // Subscribe to events
    const eventsQuery = query(collection(db, "events"));
    const unsubscribeEvents = onSnapshot(eventsQuery, (snapshot) => {
      const eventsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setEvents(eventsData);
      
      const urlParams = new URLSearchParams(window.location.search);
      const eventIdFromUrl = urlParams.get('event');
      const featuredEvent = eventsData.find(e => e.isFeatured);

      if (eventIdFromUrl) {
        const foundEvent = eventsData.find(e => e.id === eventIdFromUrl);
        setActiveEvent(foundEvent || null);
      } else {
        // Fallback to featured or first event
        setActiveEvent(featuredEvent || (eventsData.length > 0 ? eventsData[0] : null));
      }
    });

    return () => {
      unsubscribeBookings();
      unsubscribeEvents();
    };
  }, []);

  const eventDays = useMemo(() => {
    if (!activeEvent || !activeEvent.dates) return [];
    return activeEvent.dates.map(d => parse(d, 'yyyy-MM-dd', new Date()));
  }, [activeEvent]);

  const timeSlots = useMemo(() => {
    if (!selectedDate || !activeEvent || !activeEvent.timeSlots) return [];
    const slots = [];
    activeEvent.timeSlots.forEach(slot => {
        const [start, end] = slot.split('-');
        if (!start || !end) return;
        const startHour = parseInt(start.split(':')[0], 10);
        const endHour = parseInt(end.split(':')[0], 10);
        for (let hour = startHour; hour < endHour; hour++) {
            for (let m = 0; m < 60; m += 5) {
                const minute = String(m).padStart(2, '0');
                const ampm = hour >= 12 ? 'PM' : 'AM';
                let displayHour = hour % 12;
                if (displayHour === 0) displayHour = 12;
                slots.push(`${displayHour}:${minute} ${ampm}`);
            }
        }
    });
    return slots;
  }, [selectedDate, activeEvent]);

  const bookedSlotsForSelectedDate = useMemo(() => {
    if (!selectedDate || !activeEvent) return [];
    const dateKey = format(selectedDate, 'yyyy-MM-dd');
    return allBookings
      .filter(booking => booking.date === dateKey && booking.eventName === activeEvent.name)
      .map(booking => booking.time);
  }, [selectedDate, allBookings, activeEvent]);
  
  const emailsForCurrentEvent = useMemo(() => {
    if (!activeEvent) return [];
    return allBookings
        .filter(booking => booking.eventName === activeEvent.name)
        .map(booking => booking.email.toLowerCase());
  }, [allBookings, activeEvent]);

  const guestAttendance = useMemo(() => {
    const attendance = {};
    allBookings.forEach(booking => {
        const guestEmail = booking.email?.toLowerCase();
        if (!guestEmail) return;
        const eventIdentifier = booking.eventName || booking.date;
        if (!attendance[guestEmail]) attendance[guestEmail] = new Set();
        attendance[guestEmail].add(eventIdentifier);
    });
    const finalCounts = {};
    for (const guestEmail in attendance) {
        finalCounts[guestEmail] = attendance[guestEmail].size;
    }
    return finalCounts;
  }, [allBookings]);

  const groupedBookings = useMemo(() => {
    const groups = allBookings.reduce((acc, booking) => {
        const date = booking.date;
        if (!acc[date]) acc[date] = [];
        acc[date].push(booking);
        return acc;
    }, {});
    for (const date in groups) {
        groups[date].sort((a, b) => {
            const timeA = a.time.includes('M') ? convertTo24Hour(a.time) : a.time;
            const timeB = b.time.includes('M') ? convertTo24Hour(b.time) : b.time;
            return new Date(`${a.date}T${timeA}`) - new Date(`${b.date}T${timeB}`);
        });
    }
    return Object.keys(groups).sort().reduce((obj, key) => { 
        obj[key] = groups[key]; 
        return obj;
    }, {});
  }, [allBookings]);

  const handleDateSelect = (date) => {
    if (selectedDate?.getTime() !== date.getTime()) setSelectedTime(null);
    setSelectedDate(date);
    setBookingError('');
  };
  
  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setBookingError('');
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setBookingError('');
    if (emailsForCurrentEvent.includes(email.toLowerCase())) {
        setBookingError("This email has already been used to book a slot for this event.");
        return;
    }
    if (!selectedDate || !selectedTime || !name || !email) {
      setBookingError("Please fill all fields before confirming.");
      return;
    }
    try {
      const newBooking = { 
          name, 
          email: email.toLowerCase(), 
          date: format(selectedDate, 'yyyy-MM-dd'), 
          time: selectedTime, 
          checkedIn: false,
          eventName: activeEvent.name
      };
      await addDoc(collection(db, "bookings"), newBooking);
      setLastBooking({ name, email, selectedDate, selectedTime });
      setView('confirmation');
    } catch (error) {
      console.error("Error adding document: ", error);
      setBookingError("Could not save booking. Please try again.");
    }
  };
  
  const handleCheckIn = async (bookingId, currentStatus) => {
    if (!db) return;
    const bookingRef = doc(db, "bookings", bookingId);
    try {
        await updateDoc(bookingRef, { checkedIn: !currentStatus });
    } catch (error) {
        console.error("Error updating document: ", error);
    }
  };

  const resetBookingProcess = () => {
    setSelectedDate(undefined);
    setSelectedTime(null);
    setName('');
    setEmail('');
    setBookingError('');
    setView('booking');
  };

  const handleSaveEvent = async (eventData) => {
    if (!db) return;
    if (editingEvent && editingEvent.id) {
      const eventRef = doc(db, "events", editingEvent.id);
      await updateDoc(eventRef, eventData);
    } else {
      await addDoc(collection(db, "events"), {...eventData, isFeatured: false});
    }
    setEditingEvent(null);
  };

  const handleDeleteEvent = async (eventId) => {
    if (!db) return;
    if (window.confirm("Are you sure you want to delete this event? This cannot be undone.")) {
        const eventRef = doc(db, "events", eventId);
        await deleteDoc(eventRef);
    }
  };

  const handleSetFeatured = async (eventIdToFeature) => {
    if (!db) return;
    const batch = writeBatch(db);
    events.forEach(event => {
        const eventRef = doc(db, "events", event.id);
        batch.update(eventRef, { isFeatured: event.id === eventIdToFeature });
    });
    await batch.commit();
  };

  const handleUpdateBooking = async (bookingId, updatedData) => {
    if (!db) return;
    const bookingRef = doc(db, "bookings", bookingId);
    try {
        await updateDoc(bookingRef, updatedData);
        setEditingBooking(null);
    } catch (error) {
        console.error("Error updating booking:", error);
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!db) return;
    if (window.confirm("Are you sure you want to delete this booking? This action is permanent.")) {
        const bookingRef = doc(db, "bookings", bookingId);
        try {
            await deleteDoc(bookingRef);
        } catch (error) {
            console.error("Error deleting booking:", error);
        }
    }
  };

  const handleDownloadCsv = () => {
    const headers = "Date,Time,Name,Email,Event Name,Status,Past Events Count\n";
    const rows = allBookings.map(booking => {
      const pastCount = guestAttendance[booking.email?.toLowerCase()] || 0;
      const status = booking.checkedIn ? 'Checked In' : 'Registered';
      // Escape commas in strings
      const safeName = `"${booking.name?.replace(/"/g, '""') || ''}"`;
      const safeEvent = `"${booking.eventName?.replace(/"/g, '""') || ''}"`;
      return `${booking.date},${convertTo12Hour(booking.time)},${safeName},${booking.email},${safeEvent},${status},${pastCount}`;
    }).join('\n');

    const csvContent = "data:text/csv;charset=utf-8," + headers + rows;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "bookings_export.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === 'admin123') {
        setIsLoggedIn(true);
        setView('admin');
        setLoginError('');
        setPassword('');
    } else {
        setLoginError('Incorrect password. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setView('booking');
  };

  const handleShareEvent = (eventId) => {
    const url = `${window.location.origin}${window.location.pathname}?event=${eventId}`;
    const textarea = document.createElement('textarea');
    textarea.value = url;
    document.body.appendChild(textarea);
    textarea.select();
    try {
        document.execCommand('copy');
        // Simple visual feedback since alert() is discouraged
        setBookingError("Link copied to clipboard!");
        setTimeout(() => setBookingError(""), 3000);
    } catch (err) {
        console.error("Failed to copy link.");
    }
    document.body.removeChild(textarea);
  };

  const toggleList = (date) => {
    setCollapsedLists(prev => ({...prev, [date]: !prev[date]}));
  };

  const isBookingDisabled = !selectedDate || !selectedTime || !name || !email;

  if (!isFirebaseConfigValid) {
    return (
        <div className="flex items-center justify-center min-h-screen bg-red-50 p-4">
            <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-8 text-center">
                <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-gray-800 mb-2">Configuration Error</h1>
                <p className="text-gray-600">The Firebase credentials are not set correctly.</p>
            </div>
        </div>
    );
  }

  // --- Main View Router ---
  let currentView;
  if (view === 'confirmation') {
    currentView = (
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center animate-fade-in">
        <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Appointment Confirmed!</h1>
        <p className="text-gray-600 mb-6">A confirmation email has been sent to {lastBooking?.email}.</p>
        <div className="bg-gray-50 rounded-lg p-4 text-left space-y-3">
          <p className="flex items-center"><Calendar className="w-4 h-4 mr-3 text-gray-500" /><strong>Date:</strong><span className="ml-auto text-gray-700">{lastBooking && format(lastBooking.selectedDate, 'PPP')}</span></p>
          <p className="flex items-center"><Clock className="w-4 h-4 mr-3 text-gray-500" /><strong>Time:</strong><span className="ml-auto text-gray-700">{lastBooking?.selectedTime}</span></p>
          <p className="flex items-center"><User className="w-4 h-4 mr-3 text-gray-500" /><strong>Name:</strong><span className="ml-auto text-gray-700">{lastBooking?.name}</span></p>
        </div>
        <button 
            onClick={resetBookingProcess} 
            style={{ backgroundColor: themeColor }}
            className="w-full mt-8 text-white py-3 rounded-lg font-semibold hover:brightness-110 transition-all duration-300 flex items-center justify-center shadow-lg"
        >
          <ArrowLeft className="w-5 h-5 mr-2" /> Book Another Slot
        </button>
      </div>
    );
  } else if (view === 'login') {
    currentView = (
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-8 animate-fade-in">
            <h1 className="text-2xl font-bold text-gray-800 text-center mb-6">Admin Login</h1>
            <form onSubmit={handleLogin} className="space-y-4">
                <div>
                    <label htmlFor="password" className="text-sm font-medium text-gray-600">Password</label>
                    <input 
                        type="password" 
                        id="password" 
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full mt-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition"
                        required
                    />
                </div>
                {loginError && <p className="text-sm text-red-600 font-medium">{loginError}</p>}
                <div className="pt-2">
                    <button type="submit" className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition-all shadow-md">
                        Login
                    </button>
                    <button type="button" onClick={() => setView('booking')} className="w-full mt-3 text-gray-500 text-sm hover:underline">Cancel</button>
                </div>
            </form>
        </div>
    );
  } else if (view === 'admin' && isLoggedIn) {
    const checkedInCount = allBookings.filter(b => b.checkedIn).length;
    currentView = (
        <div className="w-full max-w-6xl bg-white rounded-2xl shadow-xl p-4 sm:p-8 animate-fade-in my-10">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-0">Admin Dashboard</h1>
                <div className="flex flex-wrap gap-2">
                    <button onClick={handleDownloadCsv} className="bg-green-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-green-700 transition-all duration-300 flex items-center shadow-sm">
                        <Download className="w-5 h-5 mr-2" /> Download Data
                    </button>
                    <button onClick={() => setEditingEvent({ themeColor: '#f97316' })} className="bg-blue-500 text-white py-2 px-4 rounded-lg font-semibold hover:bg-blue-600 transition-all duration-300 flex items-center shadow-sm">
                        <PlusCircle className="w-5 h-5 mr-2" /> Create Event
                    </button>
                    <button onClick={handleLogout} className="bg-gray-500 text-white py-2 px-4 rounded-lg font-semibold hover:bg-gray-600 transition-all duration-300 flex items-center shadow-sm">
                        <LogOut className="w-5 h-5 mr-2" /> Logout
                    </button>
                </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div className="bg-gray-100 p-5 rounded-2xl text-center">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Total Bookings</h3>
                    <p className="text-3xl font-black text-gray-800">{allBookings.length}</p>
                </div>
                <div className="bg-green-50 p-5 rounded-2xl text-center">
                    <h3 className="text-xs font-bold text-green-400 uppercase tracking-widest mb-1">Checked In</h3>
                    <p className="text-3xl font-black text-green-600">{checkedInCount}</p>
                </div>
            </div>

            {editingEvent ? (
                <EventForm event={editingEvent} onSave={handleSaveEvent} onCancel={() => setEditingEvent(null)} />
            ) : editingBooking ? (
                <BookingEditForm booking={editingBooking} onSave={handleUpdateBooking} onCancel={() => setEditingBooking(null)} />
            ) : (
                <div className="space-y-10">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center"><Settings className="w-5 h-5 mr-2 text-blue-500"/> Manage Events</h2>
                        <div className="grid gap-4">
                            {events.map(event => (
                                <div key={event.id} className="bg-white p-4 rounded-2xl border-2 border-gray-50 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center group hover:border-blue-100 transition-all">
                                    <div className="mb-4 sm:mb-0">
                                        <p className="font-bold text-lg flex items-center text-gray-800">
                                            {event.isFeatured && <Star className="w-5 h-5 text-yellow-500 mr-2 fill-current" />}
                                            {event.name}
                                            <span className="ml-3 w-3 h-3 rounded-full" style={{ backgroundColor: event.themeColor || '#f97316' }}></span>
                                        </p>
                                        <p className="text-xs text-gray-400 flex items-center mt-1 uppercase tracking-tighter font-semibold">
                                            <MapPin className="w-3 h-3 mr-1" /> {event.location} {event.placement ? `• ${event.placement}` : ''}
                                        </p>
                                    </div>
                                    <div className="flex items-center flex-wrap gap-2">
                                        <button onClick={() => handleSetFeatured(event.id)} disabled={event.isFeatured} className="disabled:opacity-50 disabled:cursor-not-allowed text-yellow-600 hover:text-yellow-800 font-bold flex items-center text-xs px-3 py-1.5 bg-yellow-50 rounded-lg"><Star className="w-3.5 h-3.5 mr-1"/> {event.isFeatured ? 'Featured' : 'Feature'}</button>
                                        <button onClick={() => handleShareEvent(event.id)} className="text-green-600 hover:text-green-800 font-bold flex items-center text-xs px-3 py-1.5 bg-green-50 rounded-lg"><Share2 className="w-3.5 h-3.5 mr-1"/> Share</button>
                                        <button onClick={() => setEditingEvent(event)} className="text-blue-600 hover:text-blue-800 font-bold text-xs px-3 py-1.5 bg-blue-50 rounded-lg">Edit</button>
                                        <button onClick={() => handleDeleteEvent(event.id)} className="text-red-600 hover:text-red-800 font-bold p-1.5 bg-red-50 rounded-lg"><Trash2 className="w-4 h-4"/></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div>
                        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center"><Users className="w-5 h-5 mr-2 text-green-500"/> Guest Check-in</h2>
                        {Object.keys(groupedBookings).length === 0 && <p className="text-gray-400 italic text-center py-10">No bookings yet.</p>}
                        {Object.keys(groupedBookings).map(date => (
                            <div key={date} className="mb-4">
                                <button onClick={() => toggleList(date)} className="w-full text-left flex justify-between items-center p-4 bg-gray-50 border border-gray-100 rounded-2xl shadow-sm hover:bg-white transition-all group">
                                    <span className="font-bold text-gray-700">{format(new Date(date.replace(/-/g, '/')), 'EEEE, MMMM d, yyyy')}</span>
                                    <div className="flex items-center">
                                        <span className="mr-3 px-2 py-0.5 bg-gray-200 text-gray-500 text-xs rounded-full font-bold">{groupedBookings[date].length}</span>
                                        <ChevronDown className={`w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-transform ${collapsedLists[date] ? '' : 'rotate-180'}`} />
                                    </div>
                                </button>
                                {!collapsedLists[date] && (
                                    <div className="overflow-x-auto mt-2 animate-fade-in bg-white border border-gray-100 rounded-2xl shadow-sm">
                                        <table className="min-w-full">
                                            <thead>
                                                <tr className="bg-gray-50/50">
                                                    <th className="py-3 px-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Time</th>
                                                    <th className="py-3 px-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Guest</th>
                                                    <th className="py-3 px-4 text-center text-xs font-bold text-gray-400 uppercase tracking-widest">Attendance</th>
                                                    <th className="py-3 px-4 text-center text-xs font-bold text-gray-400 uppercase tracking-widest">Status</th>
                                                    <th className="py-3 px-4 text-center text-xs font-bold text-gray-400 uppercase tracking-widest">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {groupedBookings[date].map(booking => (
                                                    <tr key={booking.id} className="hover:bg-blue-50/30 transition-colors group">
                                                        <td className="py-3 px-4 text-sm text-gray-700 font-bold whitespace-nowrap">{convertTo12Hour(booking.time)}</td>
                                                        <td className="py-3 px-4 text-sm text-gray-700">
                                                            <div className="font-bold text-gray-900">{booking.name}</div>
                                                            <div className="text-[10px] text-gray-400 font-medium tracking-tight truncate max-w-[150px]">{booking.email}</div>
                                                        </td>
                                                        <td className="py-3 px-4 text-center">
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-black bg-blue-50 text-blue-600">
                                                                <History className="w-3 h-3 mr-1"/> {guestAttendance[booking.email?.toLowerCase()] || 0}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-4 text-center">
                                                            <button 
                                                                onClick={() => handleCheckIn(booking.id, booking.checkedIn)}
                                                                className={`inline-flex items-center px-4 py-1.5 rounded-xl text-[10px] font-black tracking-widest uppercase transition-all shadow-sm ${booking.checkedIn ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}
                                                            >
                                                                {booking.checkedIn ? 'Undo' : 'Check In'}
                                                            </button>
                                                        </td>
                                                        <td className="py-3 px-4 text-center whitespace-nowrap">
                                                            <div className="flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <button onClick={() => setEditingBooking(booking)} className="text-gray-400 hover:text-blue-600 mr-3"><Edit className="w-4 h-4"/></button>
                                                                <button onClick={() => handleDeleteBooking(booking.id)} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
  } else {
    // --- Public Booking View ---
    currentView = (
        <div className="w-full max-w-4xl mx-auto py-6">
            <div className="text-center mb-8 px-4">
                <img 
                    src={activeEvent?.logoUrl || "https://static.wixstatic.com/media/ff471f_17e0b28d4dfb46cca22a04f89dc40f66~mv2.png/v1/fill/w_829,h_518,al_c,lg_1,q_90,enc_auto/The_grand_Pairie_Logo.png"} 
                    alt={activeEvent ? `${activeEvent.name} Logo` : "Event Logo"} 
                    className="mx-auto h-20 sm:h-28 w-auto object-contain drop-shadow-sm" 
                    onError={(e) => { e.target.src = "https://static.wixstatic.com/media/ff471f_17e0b28d4dfb46cca22a04f89dc40f66~mv2.png/v1/fill/w_829,h_518,al_c,lg_1,q_90,enc_auto/The_grand_Pairie_Logo.png"; }}
                />
            </div>
            {activeEvent ? (
                <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 mx-2">
                    <div className="text-center py-8 px-6 text-white relative overflow-hidden" style={{ backgroundColor: themeColor }}>
                        <div className="absolute top-0 left-0 w-full h-full opacity-20 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white/30 to-transparent pointer-events-none"></div>
                        <h1 className="text-xl sm:text-3xl font-extrabold tracking-tight leading-tight drop-shadow-sm uppercase">{activeEvent.name}</h1>
                        <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-xs sm:text-sm font-medium opacity-90">
                            <a 
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(activeEvent.location)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center px-3 py-1 bg-black/10 rounded-full backdrop-blur-md hover:bg-black/20 transition-colors cursor-pointer group"
                            >
                                <MapPin className="w-3 h-3 mr-1 group-hover:scale-110 transition-transform" /> {activeEvent.location}
                            </a>
                            {activeEvent.placement && (
                                <span className="flex items-center px-3 py-1 bg-white/20 rounded-full backdrop-blur-md">
                                    {activeEvent.placement}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="p-6 sm:p-10 md:p-14">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                            <div>
                                <h2 className="text-2xl font-black text-gray-900 mb-8 flex items-center tracking-tight">
                                    <span className="w-9 h-9 rounded-2xl flex items-center justify-center text-white mr-4 text-base shadow-lg shadow-black/10" style={{ backgroundColor: themeColor }}>1</span>
                                    Select Date
                                </h2>
                                <div className="space-y-4">
                                    {eventDays.map((day) => {
                                        const isSelected = selectedDate?.getTime() === day.getTime();
                                        return (
                                            <button 
                                                key={day.toString()} 
                                                onClick={() => handleDateSelect(day)} 
                                                className={`w-full text-left p-6 rounded-2xl border-2 transition-all duration-300 flex items-center justify-between ${isSelected ? 'border-transparent shadow-2xl scale-[1.02] text-white' : 'bg-gray-50/50 border-gray-100 hover:border-blue-200 hover:bg-white text-gray-700'}`}
                                                style={isSelected ? { backgroundColor: themeColor } : {}}
                                            >
                                                <div className="font-black text-xl tracking-tight">{format(day, 'EEEE, MMM d')}</div>
                                                {isSelected ? <CheckCircle2 className="w-7 h-7" /> : <div className="w-7 h-7 rounded-full border-2 border-gray-200"></div>}
                                            </button>
                                        );
                                    })}
                                </div>

                                {selectedDate && (
                                    <div className="mt-12 animate-fade-in">
                                        <h2 className="text-2xl font-black text-gray-900 mb-8 flex items-center tracking-tight">
                                            <span className="w-9 h-9 rounded-2xl flex items-center justify-center text-white mr-4 text-base shadow-lg shadow-black/10" style={{ backgroundColor: themeColor }}>2</span>
                                            Pick a Time
                                        </h2>
                                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                                            {timeSlots.map((time) => {
                                                const time24h = convertTo24Hour(time);
                                                const isBooked = bookedSlotsForSelectedDate.includes(time) || bookedSlotsForSelectedDate.includes(time24h);
                                                const isSelected = selectedTime === time;
                                                return (
                                                    <button 
                                                        key={time} 
                                                        onClick={() => handleTimeSelect(time)} 
                                                        disabled={isBooked} 
                                                        className={`py-3.5 rounded-2xl text-[11px] font-black tracking-widest uppercase transition-all duration-300 border-2 ${isBooked ? 'bg-gray-50 text-gray-200 border-transparent cursor-not-allowed line-through' : isSelected ? 'text-white border-transparent shadow-xl transform scale-110' : 'bg-white text-gray-700 border-gray-100 hover:border-gray-300'}`}
                                                        style={isSelected ? { backgroundColor: themeColor } : {}}
                                                    >
                                                        {time}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {timeSlots.length > 0 && timeSlots.length === bookedSlotsForSelectedDate.length && (
                                            <div className="mt-6 p-4 bg-red-50 rounded-2xl border border-red-100 text-center">
                                                <p className="text-red-600 font-bold text-sm tracking-tight">This day is fully booked! Try another date.</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="lg:border-l-2 lg:pl-12 border-gray-50 flex flex-col">
                                <div className={`transition-all duration-500 ${selectedTime ? 'opacity-100 transform translate-y-0' : 'opacity-30 pointer-events-none translate-y-6'}`}>
                                    <h2 className="text-2xl font-black text-gray-900 mb-8 flex items-center tracking-tight">
                                        <span className="w-9 h-9 rounded-2xl flex items-center justify-center text-white mr-4 text-base shadow-lg shadow-black/10" style={{ backgroundColor: themeColor }}>3</span>
                                        Register Now
                                    </h2>
                                    <form onSubmit={handleBookingSubmit} className="space-y-6">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Guest Name</label>
                                            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full p-5 bg-gray-50 border-transparent rounded-2xl font-bold text-gray-800 placeholder-gray-300 focus:bg-white focus:ring-4 transition-all" placeholder="Enter full name" required style={{ '--tw-ring-color': themeColor + '22' }} />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Email Address</label>
                                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-5 bg-gray-50 border-transparent rounded-2xl font-bold text-gray-800 placeholder-gray-300 focus:bg-white focus:ring-4 transition-all" placeholder="name@email.com" required style={{ '--tw-ring-color': themeColor + '22' }} />
                                        </div>
                                        {bookingError && (
                                            <div className="flex items-center text-red-600 bg-red-50 p-5 rounded-2xl border border-red-100 animate-shake">
                                                <XCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                                                <p className="text-sm font-bold tracking-tight">{bookingError}</p>
                                            </div>
                                        )}
                                        <button 
                                            type="submit" 
                                            disabled={isBookingDisabled} 
                                            className="w-full text-white py-6 rounded-2xl font-black text-xl tracking-tighter uppercase hover:brightness-110 shadow-2xl transition-all duration-300 disabled:bg-gray-100 disabled:text-gray-300 disabled:shadow-none transform hover:-translate-y-1 active:translate-y-0"
                                            style={{ backgroundColor: themeColor }}
                                        >
                                            Confirm Appointment
                                        </button>
                                    </form>
                                </div>
                                {!selectedDate && <div className="flex-grow flex flex-col items-center justify-center text-center text-gray-300 p-8 space-y-4">
                                    <div className="w-20 h-20 rounded-3xl bg-gray-50 flex items-center justify-center"><Calendar className="w-8 h-8 opacity-20" /></div>
                                    <p className="font-bold tracking-tight">Select a date above to begin your registration</p>
                                </div>}
                                {selectedDate && !selectedTime && <div className="flex-grow flex flex-col items-center justify-center text-center text-gray-300 p-8 space-y-4 animate-pulse">
                                    <div className="w-20 h-20 rounded-3xl bg-gray-50 flex items-center justify-center"><Clock className="w-8 h-8 opacity-20" /></div>
                                    <p className="font-bold tracking-tight">Now pick an available time slot</p>
                                </div>}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-[3rem] shadow-2xl p-20 text-center mx-4 border border-gray-50">
                    <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner">
                        <Calendar className="w-10 h-10 text-gray-200" />
                    </div>
                    <h1 className="text-3xl font-black text-gray-900 tracking-tighter uppercase">No Live Events</h1>
                    <p className="text-gray-400 mt-4 max-w-sm mx-auto font-medium text-lg leading-relaxed">The event portal is currently closed. Please check back with us soon for new dates!</p>
                </div>
            )}
        </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-[#fafafa] text-gray-900 font-fira overflow-x-hidden selection:bg-blue-100 selection:text-blue-900">
        <header className="fixed top-8 right-8 z-[100]">
            <button 
                onClick={() => setView(isLoggedIn ? 'admin' : 'login')} 
                className="p-4 bg-white/70 backdrop-blur-xl rounded-[2rem] shadow-2xl border border-white hover:scale-110 transition-all active:scale-90 group"
            >
                <Shield className="h-6 w-6 text-gray-300 group-hover:text-blue-500 transition-colors" />
            </button>
        </header>
        <main className="flex-grow flex items-center justify-center p-4">
            {currentView}
        </main>
        <footer className="text-center py-10 px-4">
            <p className="text-[10px] font-black text-gray-300 uppercase tracking-[0.4em] mb-2">Developed With Love</p>
            <a href="https://www.lvbranding.com" target="_blank" rel="noopener noreferrer" className="text-xs font-black text-gray-500 hover:text-black transition-colors underline decoration-gray-200 underline-offset-8">LV Branding</a>
        </footer>
        <style dangerouslySetInnerHTML={{ __html: `
            .font-fira { font-family: 'Fira Sans', sans-serif; }
            .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
            .animate-shake { animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both; }
            @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            @keyframes shake { 10%, 90% { transform: translate3d(-1px, 0, 0); } 20%, 80% { transform: translate3d(2px, 0, 0); } 30%, 50%, 70% { transform: translate3d(-4px, 0, 0); } 40%, 60% { transform: translate3d(4px, 0, 0); } }
        `}} />
    </div>
  );
}

// --- Event Form Component ---
const EventForm = ({ event, onSave, onCancel }) => {
    const [name, setName] = useState(event.name || '');
    const [location, setLocation] = useState(event.location || '');
    const [placement, setPlacement] = useState(event.placement || '');
    const [themeColor, setThemeColor] = useState(event.themeColor || '#f97316');
    const [dates, setDates] = useState(event.dates ? event.dates.join(', ') : '');
    const [logoUrl, setLogoUrl] = useState(event.logoUrl || '');
    
    const formatTimeSlotsForDisplay = (slots) => {
        if (!slots) return '';
        return slots.map(slot => {
            const parts = slot.split('-');
            if (parts.length !== 2) return slot;
            const [start, end] = parts;
            const start12 = convertTo12Hour(start).replace(':00', '');
            const end12 = convertTo12Hour(end).replace(':00', '');
            return `${start12}-${end12}`;
        }).join(', ');
    };

    const [timeSlots, setTimeSlots] = useState(formatTimeSlotsForDisplay(event.timeSlots));
    const [formError, setFormError] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormError('');
        try {
            // Robust parsing to handle various inputs (e.g. 10AM, 10:00AM, 10 AM)
            const formattedTimeSlots = timeSlots.split(',').map(t => {
                const parts = t.trim().split('-');
                if (parts.length !== 2) throw new Error(`Format error: ${t}`);
                
                const [start, end] = parts;
                const normalize = (val) => val.trim().replace(/\s/g, '').toUpperCase();
                
                let start24, end24;
                
                // Try format like 10AM
                start24 = parse(normalize(start), 'ha', new Date());
                end24 = parse(normalize(end), 'ha', new Date());

                if (isNaN(start24.getTime()) || isNaN(end24.getTime())) {
                    // Try format like 10:30AM
                    start24 = parse(normalize(start), 'h:mma', new Date());
                    end24 = parse(normalize(end), 'h:mma', new Date());
                    if (isNaN(start24.getTime()) || isNaN(end24.getTime())) {
                        throw new Error(`Time value: "${t}". Use "10AM-2PM" or "10:30AM-2:15PM".`);
                    }
                }

                return `${format(start24, 'HH:mm')}-${format(end24, 'HH:mm')}`;
            });

            onSave({
                name,
                location,
                placement,
                themeColor,
                dates: dates.split(',').map(d => d.trim()),
                timeSlots: formattedTimeSlots,
                logoUrl
            });
        } catch (error) {
            setFormError(error.message);
        }
    };

    return (
        <div className="bg-gray-50/50 p-8 rounded-[2rem] border-2 border-gray-100 animate-fade-in shadow-inner">
            <h2 className="text-2xl font-black mb-8 tracking-tighter uppercase text-gray-800">{event.id ? 'Refine Event Profile' : 'Initialize New Campaign'}</h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                    <div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Campaign Name</label>
                        <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full p-4 bg-white border-2 border-gray-50 rounded-2xl font-bold focus:ring-4 focus:border-blue-500 transition-all" required />
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Geography / City</label>
                        <input type="text" value={location} onChange={e => setLocation(e.target.value)} className="w-full p-4 bg-white border-2 border-gray-50 rounded-2xl font-bold focus:ring-4 focus:border-blue-500 transition-all" placeholder="e.g. Austin, TX" required />
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Specific Placement</label>
                        <input type="text" value={placement} onChange={e => setPlacement(e.target.value)} className="w-full p-4 bg-white border-2 border-gray-50 rounded-2xl font-bold focus:ring-4 focus:border-blue-500 transition-all" placeholder="e.g. Hall C, Booth 500" />
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2 flex items-center"><Palette className="w-3 h-3 mr-1" /> Brand Identity Color</label>
                        <div className="flex gap-3">
                            <input type="color" value={themeColor} onChange={e => setThemeColor(e.target.value)} className="w-16 h-14 p-1 bg-white border-2 border-gray-50 rounded-2xl cursor-pointer" />
                            <input type="text" value={themeColor} onChange={e => setThemeColor(e.target.value)} className="flex-grow p-4 bg-white border-2 border-gray-50 rounded-2xl font-mono font-bold uppercase" />
                        </div>
                    </div>
                </div>
                <div className="space-y-6">
                    <div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Logo Asset URL</label>
                        <input type="text" value={logoUrl} onChange={e => setLogoUrl(e.target.value)} className="w-full p-4 bg-white border-2 border-gray-50 rounded-2xl font-bold focus:ring-4 focus:border-blue-500 transition-all" placeholder="https://..." />
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Activation Dates (YYYY-MM-DD)</label>
                        <input type="text" value={dates} onChange={e => setDates(e.target.value)} className="w-full p-4 bg-white border-2 border-gray-50 rounded-2xl font-bold focus:ring-4 focus:border-blue-500 transition-all" placeholder="2025-10-11, 2025-10-12" required />
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Availability Cycles (12h Ranges)</label>
                        <input type="text" value={timeSlots} onChange={e => setTimeSlots(e.target.value)} className="w-full p-4 bg-white border-2 border-gray-50 rounded-2xl font-bold focus:ring-4 focus:border-blue-500 transition-all" placeholder="10AM-2PM, 5PM-8PM" required />
                    </div>
                    {formError && <div className="text-xs text-red-600 font-black bg-red-50 p-4 rounded-2xl border border-red-100">{formError}</div>}
                    <div className="flex justify-end gap-3 pt-4">
                        <button type="button" onClick={onCancel} className="px-8 py-4 bg-white text-gray-400 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-gray-100 transition-all">Discard</button>
                        <button type="submit" className="px-8 py-4 bg-black text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-gray-900 transition-all shadow-xl shadow-black/10">Commit Settings</button>
                    </div>
                </div>
            </form>
        </div>
    );
};

// --- Booking Edit Form Component ---
const BookingEditForm = ({ booking, onSave, onCancel }) => {
    const [name, setName] = useState(booking.name);
    const [email, setEmail] = useState(booking.email);
    const [time, setTime] = useState(booking.time);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(booking.id, { name, email, time });
    };

    return (
        <div className="bg-blue-50/50 p-8 rounded-[2rem] border-2 border-blue-100 animate-fade-in shadow-inner">
            <h3 className="text-xl font-black text-blue-900 mb-6 tracking-tighter uppercase">Manual Guest Override</h3>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-3 gap-6 items-end">
                <div>
                    <label className="text-[10px] font-black text-blue-400 uppercase tracking-widest block mb-2">Guest Name</label>
                    <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full p-4 bg-white border-2 border-blue-50 rounded-2xl font-bold text-blue-900" required />
                </div>
                <div>
                    <label className="text-[10px] font-black text-blue-400 uppercase tracking-widest block mb-2">Electronic Mail</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full p-4 bg-white border-2 border-blue-50 rounded-2xl font-bold text-blue-900" required />
                </div>
                <div>
                    <label className="text-[10px] font-black text-blue-400 uppercase tracking-widest block mb-2">Time Pointer</label>
                    <input type="text" value={time} onChange={e => setTime(e.target.value)} className="w-full p-4 bg-white border-2 border-blue-50 rounded-2xl font-bold text-blue-900" required />
                </div>
                <div className="sm:col-span-3 flex justify-end gap-3 mt-4">
                    <button type="button" onClick={onCancel} className="px-8 py-3 text-blue-400 font-black uppercase tracking-widest text-[10px] hover:text-blue-600 transition-all">Discard Changes</button>
                    <button type="submit" className="px-10 py-4 bg-blue-600 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-xl shadow-blue-200 hover:bg-blue-700 transition-all">Apply Override</button>
                </div>
            </form>
        </div>
    );
};
